import React, { useState, useEffect, useRef, useCallback } from 'react'
import '../../styles/TicketsPage.css'

const STATUS_STYLE = {
  offline:          { bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF', label: 'Offline' },
  on_call:          { bg: '#DCFCE7', color: '#16A34A', dot: '#22C55E', label: 'On Call' },
  ready:            { bg: '#DBEAFE', color: '#1D4ED8', dot: '#3B82F6', label: 'Ready' },
  busy:             { bg: '#FEF3C7', color: '#D97706', dot: '#F59E0B', label: 'Busy' },
  short_break:      { bg: '#FEF3C7', color: '#D97706', dot: '#F59E0B', label: 'Short Break' },
  lunch:            { bg: '#FEE2E2', color: '#DC2626', dot: '#EF4444', label: 'Lunch' },
  training_session: { bg: '#EDE9FE', color: '#7C3AED', dot: '#8B5CF6', label: 'Training Session' },
  quality_feedback: { bg: '#EDE9FE', color: '#7C3AED', dot: '#8B5CF6', label: 'Quality Feedback' },
}

const getStatusStyle = (raw) => {
  const key = String(raw || '').toLowerCase().replace(/\s+/g, '_')
  return STATUS_STYLE[key] || STATUS_STYLE['offline']
}

const PERIODS = [
  { label: 'Today',        value: 'today' },
  { label: 'Last 7 Days',  value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'Custom',       value: 'custom' },
]

const PAGE_SIZE_OPTIONS = [10, 25, 50]

function buildPageNumbers(current, total) {
  if (total <= 1) return [1]
  const pages = [1]
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
  if (current < total - 2) pages.push('...')
  if (total > 1) pages.push(total)
  return pages
}

function DateFilter({ period, from, to, onChangePeriod, onChangeRange }) {
  const [open, setOpen] = useState(false)
  const [localFrom, setLocalFrom] = useState(from)
  const [localTo, setLocalTo] = useState(to)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const displayLabel = () => {
    if (period === 'custom' && from && to) {
      const fmt = d => d.split('-').reverse().join('/')
      return `${fmt(from)} – ${fmt(to)}`
    }
    return PERIODS.find(p => p.value === period)?.label || 'Today'
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', border: '1px solid #D1D5DB',
          borderRadius: 8, background: '#fff', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, color: '#374151',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="#6B7280" strokeWidth="1.8">
          <rect x="3" y="4" width="14" height="13" rx="2" />
          <path d="M3 8h14M7 2v4M13 2v4" />
        </svg>
        {displayLabel()}
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="#6B7280" strokeWidth="2">
          <path d="M5 8l5 5 5-5" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 100,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 200, overflow: 'hidden',
        }}>
          {PERIODS.filter(p => p.value !== 'custom').map(p => (
            <div
              key={p.value}
              onClick={() => { onChangePeriod(p.value); setOpen(false) }}
              style={{
                padding: '9px 14px', fontSize: 12, cursor: 'pointer',
                fontWeight: period === p.value ? 700 : 400,
                color: period === p.value ? '#1A6FA8' : '#374151',
                background: period === p.value ? '#EFF6FF' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = period === p.value ? '#EFF6FF' : '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = period === p.value ? '#EFF6FF' : 'transparent'}
            >
              {p.label}
            </div>
          ))}
          <div style={{ borderTop: '1px solid #E5E7EB', padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Custom Range</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="date" value={localFrom}
                onChange={e => setLocalFrom(e.target.value)}
                style={{ fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, padding: '4px 6px', flex: 1 }}
              />
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>–</span>
              <input
                type="date" value={localTo}
                onChange={e => setLocalTo(e.target.value)}
                style={{ fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, padding: '4px 6px', flex: 1 }}
              />
            </div>
            <button
              onClick={() => { if (localFrom && localTo) { onChangeRange(localFrom, localTo); setOpen(false) } }}
              disabled={!localFrom || !localTo}
              style={{
                marginTop: 8, width: '100%', padding: '6px 0',
                background: localFrom && localTo ? '#1A6FA8' : '#E5E7EB',
                color: localFrom && localTo ? '#fff' : '#9CA3AF',
                border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: localFrom && localTo ? 'pointer' : 'default',
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const STATUS_FILTER_OPTIONS = [
  { label: 'All Status',       value: '' },
  { label: 'Ready',            value: 'ready' },
  { label: 'Busy',             value: 'busy' },
  { label: 'Short Break',      value: 'short_break' },
  { label: 'Lunch',            value: 'lunch' },
  { label: 'Training Session', value: 'training_session' },
  { label: 'Quality Feedback', value: 'quality_feedback' },
  { label: 'Offline',          value: 'offline' },
]

const todayStr = () => new Date().toISOString().slice(0, 10)

const periodToRange = (p) => {
  const t = new Date()
  const fmt = d => d.toISOString().slice(0, 10)
  if (p === 'last_7_days')  { const f = new Date(t); f.setDate(f.getDate() - 6);  return { from: fmt(f), to: fmt(t) } }
  if (p === 'last_30_days') { const f = new Date(t); f.setDate(f.getDate() - 29); return { from: fmt(f), to: fmt(t) } }
  return { from: fmt(t), to: fmt(t) } // today / fallback
}

export default function AgentPage({ fetchJson }) {
  const [agents, setAgents]         = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)
  const [search, setSearch]         = useState('')
  const [period, setPeriod]         = useState('today')
  const [fromDate, setFromDate]     = useState(todayStr())
  const [toDate, setToDate]         = useState(todayStr())
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage]             = useState(1)
  const [pageSize, setPageSize]     = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  // Reset to page 1 on filter/search changes
  useEffect(() => { setPage(1) }, [search, period, fromDate, toDate, statusFilter, pageSize])

  const buildParams = useCallback(() => {
    const p = new URLSearchParams()
    const { from, to } = period === 'custom' ? { from: fromDate, to: toDate } : periodToRange(period)
    p.set('from_date', from)
    p.set('to_date', to)
    if (search.trim())  p.set('q', search.trim())
    if (statusFilter)   p.set('status', statusFilter)
    p.set('page', String(page))
    p.set('page_size', String(pageSize))
    return p.toString()
  }, [period, fromDate, toDate, search, statusFilter, page, pageSize])

  useEffect(() => {
    if (!fetchJson) return
    setLoading(true)
    setError(null)
    fetchJson(`/api/agents/agent/agent-summary/?${buildParams()}`)
      .then(data => {
        const results = Array.isArray(data) ? data : (data?.results ?? [])
        setAgents(results)
        setTotalCount(Array.isArray(data) ? results.length : (data?.count ?? results.length))
      })
      .catch(() => setError('Failed to load agent data.'))
      .finally(() => setLoading(false))
  }, [fetchJson, buildParams])

  const handlePeriod = (p) => {
    setPeriod(p)
    const { from, to } = periodToRange(p)
    setFromDate(from)
    setToDate(to)
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const periodLabel = period === 'custom' ? 'Period' : PERIODS.find(p => p.value === period)?.label

  const COL_HEADERS = [
    {
      label: 'Agent',
      icon: <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M13 6a4 4 0 11-8 0 4 4 0 018 0zM3 18a7 7 0 0114 0" /></svg>,
      iconBg: '#EDE9FE', iconColor: '#7C3AED',
    },
    {
      label: 'Call Activity',
      icon: <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 10h3l2-7 4 14 2-7h5" /></svg>,
      iconBg: '#DCFCE7', iconColor: '#16A34A',
    },
    {
      label: 'Active Time / Day',
      icon: <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="10" cy="10" r="8"/><path d="M10 6v4l3 2"/></svg>,
      iconBg: '#EDE9FE', iconColor: '#7C3AED',
    },
    {
      label: 'Talk Time / Day',
      icon: <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6.5 4S5 4 4 6s1 5 3 7 5 4 7 3 2-2.5 2-2.5l-3-3-2 1-3-4 1-2-3-3z"/></svg>,
      iconBg: '#DBEAFE', iconColor: '#1D4ED8',
    },
    {
      label: 'Break Time / Day',
      icon: <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 3h10v8a5 5 0 01-10 0V3z"/><path d="M15 7h2a2 2 0 010 4h-2"/><path d="M8 17h4"/><path d="M10 13v4"/></svg>,
      iconBg: '#FEF3C7', iconColor: '#D97706',
    },
    {
      label: 'Tickets',
      icon: <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="14" height="13" rx="2"/><path d="M7 8h6M7 12h4"/></svg>,
      iconBg: '#E0F2FE', iconColor: '#0369A1',
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>

      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>Agent Activity</span>
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="#9CA3AF" strokeWidth="1.8">
          <circle cx="10" cy="10" r="8"/><path d="M10 9v5M10 7h.01"/>
        </svg>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <svg
            width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="#9CA3AF" strokeWidth="2"
            style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          >
            <circle cx="9" cy="9" r="6"/><path d="M15 15l-3.5-3.5"/>
          </svg>
          <input
            type="text"
            placeholder="Search agent…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '7px 10px 7px 30px', border: '1px solid #E5E7EB',
              borderRadius: 8, fontSize: 12, color: '#374151',
              outline: 'none', width: 180, background: '#fff',
            }}
          />
        </div>

        <div className="t360-select-wrap" style={{ minWidth: 145 }}>
          <select
            className="t360-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="t360-select-arrow">▼</span>
        </div>

        <DateFilter
          period={period}
          from={fromDate}
          to={toDate}
          onChangePeriod={handlePeriod}
          onChangeRange={(f, t) => { setPeriod('custom'); setFromDate(f); setToDate(t) }}
        />

        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6B7280' }}>
          {!loading && <><strong style={{ color: '#111827' }}>{totalCount}</strong> agent{totalCount !== 1 ? 's' : ''}</>}
        </span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff' }}>
        {error ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#EF4444', fontSize: 13 }}>{error}</div>
        ) : loading ? (
          <div style={{ padding: 48, textAlign: 'center', fontSize: 13, color: '#6B7280' }}>Loading agents…</div>
        ) : agents.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>No agents found</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Try adjusting your search or date filter</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ padding: '12px 14px', width: 40, textAlign: 'center', color: '#9CA3AF', fontWeight: 500, fontSize: 11 }}>#</th>
                {COL_HEADERS.map(col => (
                  <th key={col.label} style={{ padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                        background: col.iconBg, color: col.iconColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {col.icon}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {col.label}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, idx) => {
                const st = getStatusStyle(agent.agent_activity?.current_status)
                return (
                  <tr
                    key={idx}
                    style={{ borderBottom: '1px solid #F3F4F6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 14px', textAlign: 'center', color: '#9CA3AF', fontWeight: 500, fontSize: 11 }}>
                      {(page - 1) * pageSize + idx + 1}
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1A6FA8, #2B8FD8)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontWeight: 700, fontSize: 11, flexShrink: 0,
                        }}>
                          {(agent.name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600, color: '#111827' }}>{agent.name || '—'}</span>
                      </div>
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 20, alignSelf: 'flex-start',
                          background: st.bg, color: st.color, fontWeight: 600, fontSize: 11,
                        }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot }} />
                          {st.label}
                        </span>
                        {agent.agent_activity?.last_call_time && (
                          <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                            {agent.agent_activity.last_call_time}
                          </span>
                        )}
                      </div>
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontWeight: 600, color: agent.active_time?.minutes > 0 ? '#16A34A' : '#9CA3AF' }}>
                        {agent.active_time?.display || '—'}
                      </span>
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontWeight: 600, color: agent.talktime?.minutes > 0 ? '#1D4ED8' : '#9CA3AF' }}>
                        {agent.talktime?.display || '—'}
                      </span>
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontWeight: 600, color: agent.break_time?.minutes > 0 ? '#D97706' : '#9CA3AF' }}>
                        {agent.break_time?.display || '—'}
                      </span>
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      {agent.total_tickets > 0 ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 10px',
                          background: '#EFF6FF', color: '#1D4ED8',
                          borderRadius: 20, fontWeight: 700, fontSize: 11,
                        }}>
                          {agent.total_tickets}
                        </span>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>0</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="t360-pagination">
        {totalPages > 1 && (
          <>
            <button
              className="t360-page-btn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1 || loading}
            >
              ← Prev
            </button>

            {buildPageNumbers(page, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`e-${i}`} className="t360-page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`t360-page-btn${p === page ? ' active' : ''}`}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="t360-page-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages || loading}
            >
              Next →
            </button>

            <span className="t360-page-info">Page {page} of {totalPages}</span>
          </>
        )}

        <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
          Total: <strong style={{ color: '#111827' }}>{totalCount}</strong> agent{totalCount !== 1 ? 's' : ''}
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>Rows per page:</span>
          <div className="t360-select-wrap" style={{ minWidth: 70 }}>
            <select
              className="t360-select"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              disabled={loading}
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="t360-select-arrow">▼</span>
          </div>
        </div>
      </div>
    </div>
  )
}
